import type { LocationQuery } from 'vue-router';
import { parseNumber } from '@/utilities/index.ts';
import { getCity, getRegion } from '@/api';
import type { GeoCoordinates, Location, LocationKind } from '@/views/search/types.ts';
import { Semaphore } from '@/utilities/semaphore.ts';

type CacheKey = string;
type CacheValue = Location | undefined;

const semaphore = new Semaphore();
const locationCache = new Map<CacheKey, CacheValue>();

function generateCacheKey(query: LocationQuery): CacheKey {
  return `${query.searchLocationId ?? 'null'}:${query.searchLocationName ?? 'null'}:${query.searchLocationKind ?? 'null'}:${query.location ?? 'null'}:${query.regionId ?? 'null'}:${query.cityId ?? 'null'}`;
}

function parseQueryString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function parseCoordinates(value: unknown): GeoCoordinates | undefined {
  const coordinates = parseQueryString(value);
  if (!coordinates) return undefined;

  const parts = coordinates.split(',');
  if (parts.length !== 2) return undefined;

  const latitude = Number(parts[0]?.trim());
  const longitude = Number(parts[1]?.trim());

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return undefined;

  return { latitude, longitude };
}

function getKind(query: LocationQuery): LocationKind | undefined {
  const explicitKind = parseQueryString(query.searchLocationKind);
  if (explicitKind === 'city' || explicitKind === 'region' || explicitKind === 'location') return explicitKind;

  if (parseNumber(query.cityId)) return 'city';
  if (parseNumber(query.regionId)) return 'region';
  if (parseCoordinates(query.location)) return 'location';
  return undefined;
}

async function fetchRegionGenitiveName(regionId: number): Promise<string | undefined> {
  try {
    const fetch = await getRegion({ path: { regionId } });
    return fetch.response?.status === 200 && fetch.data ? fetch.data.genitiveName : undefined;
  } catch {
    return undefined;
  }
}

export async function fetchLocation(query: LocationQuery): Promise<Location | undefined> {
  await semaphore.acquire();
  try {
    const cacheKey = generateCacheKey(query);

    if (locationCache.has(cacheKey)) {
      return locationCache.get(cacheKey);
    }

    const searchLocationId = parseNumber(query.searchLocationId);
    const searchLocationName = parseQueryString(query.searchLocationName);
    const searchLocationTypeLabel = parseQueryString(query.searchLocationTypeLabel) ?? '';
    const coordinates = parseCoordinates(query.location);
    const cityId = parseNumber(query.cityId);
    const regionId = parseNumber(query.regionId);
    const kind = getKind(query);

    let result: Location | undefined;

    if (searchLocationId && searchLocationName && kind) {
      result = {
        id: searchLocationId,
        name: searchLocationName,
        kind,
        typeLabel: searchLocationTypeLabel,
        coordinates,
        cityId: cityId ?? undefined,
        regionId: regionId ?? undefined,
        genitiveName: kind === 'region' && regionId ? await fetchRegionGenitiveName(regionId) : undefined,
      };
    } else if (cityId) {
      const fetch = await getCity({ path: { cityId } });
      if (fetch.response?.status === 200 && fetch.data) {
        result = {
          id: cityId,
          name: fetch.data.name,
          kind: 'city',
          typeLabel: '',
          cityId,
        };
      } else {
        throw Error(`Could not retrieve city: ${fetch.error}`);
      }
    } else if (regionId) {
      const fetch = await getRegion({ path: { regionId } });
      if (fetch.response?.status === 200 && fetch.data) {
        result = {
          id: regionId,
          name: fetch.data.name,
          kind: 'region',
          typeLabel: '',
          regionId,
          genitiveName: fetch.data.genitiveName,
        };
      } else {
        throw Error(`Could not retrieve region: ${fetch.error}`);
      }
    }

    locationCache.set(cacheKey, result);

    return result;
  } finally {
    semaphore.release();
  }
}
